import React from 'react'
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '@/hooks/useUsers'
import { useUserStore } from '@/stores/userStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PlusIcon, Pencil1Icon, TrashIcon, ReloadIcon } from '@/components/Icons'

export default function UsersPage() {
  const { data: users, isLoading, error, refetch } = useUsers()
  const createUserMutation = useCreateUser()
  const updateUserMutation = useUpdateUser()
  const deleteUserMutation = useDeleteUser()
  
  const {
    isUserFormOpen,
    editingUser,
    userFormData,
    openUserForm,
    closeUserForm,
    setEditingUser,
    updateUserFormData,
    resetUserForm
  } = useUserStore()

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingUser) {
        await updateUserMutation.mutateAsync({
          id: editingUser.id,
          data: userFormData
        })
      } else {
        await createUserMutation.mutateAsync(userFormData)
      }
      closeUserForm()
    } catch (error) {
      console.error('Error saving user:', error)
    }
  }

  const handleEdit = (user) => {
    setEditingUser(user)
  }

  const handleDelete = async (user) => {
    if (confirm(`정말로 ${user.name}님을 삭제하시겠습니까?`)) {
      try {
        await deleteUserMutation.mutateAsync(user.id)
      } catch (error) {
        console.error('Error deleting user:', error)
      }
    }
  }

  const getRoleText = (role) => {
    switch (role) {
      case 'admin': return '관리자'
      case 'manager': return '매니저'
      case 'user': return '사용자'
      default: return role
    }
  }

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        활성
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        비활성
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <ReloadIcon className="h-4 w-4 animate-spin" />
          <div className="text-lg">사용자 목록을 불러오는 중...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-lg text-red-600">사용자 목록을 불러오는데 실패했습니다.</div>
        <Button onClick={() => refetch()}>다시 시도</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">사용자 관리</h1>
          <p className="text-muted-foreground">
            시스템 사용자를 관리하고 권한을 설정하세요.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <ReloadIcon className="mr-2 h-4 w-4" />
            새로고침
          </Button>
          <Button onClick={openUserForm}>
            <PlusIcon className="mr-2 h-4 w-4" />
            사용자 추가
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>전체 사용자</CardTitle>
          <CardDescription>
            시스템에 등록된 모든 사용자를 확인하고 관리할 수 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>이름</TableHead>
                <TableHead>이메일</TableHead>
                <TableHead>권한</TableHead>
                <TableHead>상태</TableHead>
                <TableHead>생성일</TableHead>
                <TableHead className="text-right">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleText(user.role)}</TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell>{user.createdAt}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        disabled={updateUserMutation.isPending}
                      >
                        <Pencil1Icon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user)}
                        disabled={deleteUserMutation.isPending}
                        className="text-red-600 hover:text-red-700"
                      >
                        {deleteUserMutation.isPending ? (
                          <ReloadIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isUserFormOpen && (
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>
              {editingUser ? '사용자 수정' : '사용자 추가'}
            </CardTitle>
            <CardDescription>
              사용자 정보를 입력하세요.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">이름</Label>
                <Input
                  id="name"
                  value={userFormData.name}
                  onChange={(e) => updateUserFormData({ name: e.target.value })}
                  disabled={createUserMutation.isPending || updateUserMutation.isPending}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">이메일</Label>
                <Input
                  id="email"
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => updateUserFormData({ email: e.target.value })}
                  disabled={createUserMutation.isPending || updateUserMutation.isPending}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">권한</Label>
                <select
                  id="role"
                  value={userFormData.role}
                  onChange={(e) => updateUserFormData({ role: e.target.value })}
                  disabled={createUserMutation.isPending || updateUserMutation.isPending}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                >
                  <option value="user">사용자</option>
                  <option value="manager">매니저</option>
                  <option value="admin">관리자</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={createUserMutation.isPending || updateUserMutation.isPending}
                >
                  {(createUserMutation.isPending || updateUserMutation.isPending) ? (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {editingUser ? '수정' : '추가'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeUserForm}
                  disabled={createUserMutation.isPending || updateUserMutation.isPending}
                >
                  취소
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  )
}